import React, { useState, useEffect, useMemo } from "react";
import { FiX, FiCheck, FiSearch, FiCreditCard, FiUsers } from "react-icons/fi";
import { userRequest } from "../utils/requestMethods";
import { generateStaffIDCard, PRINT_SHEET_SIZE_MM } from "../utils/idCardGenerator";
import BulkIDGenerationProgressModal from "./BulkIDGenerationProgressModal";
import jsPDF from "jspdf";
import { format } from "date-fns";

const ROLE_LABELS = {
  0: "Admin",
  1: "Teacher",
  2: "Bursar",
  3: "Academic Admin",
  admin: "Admin",
  teacher: "Teacher",
  burser: "Bursar",
  academic_admin: "Academic Admin",
};

// Places each front + back sheet at its true print size (crop marks line up when cut),
// as many per A4 page as fit. onCardAdded(index) is awaited after each card.
const buildStaffIDCardsPdf = async (idCards, onCardAdded) => {
  const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageWidth = 210;
  const pageHeight = 297;
  const margin = 10;
  const gap = 6;
  const { width, height } = PRINT_SHEET_SIZE_MM;
  const perPage = Math.max(1, Math.floor((pageHeight - margin * 2 + gap) / (height + gap)));
  const x = (pageWidth - width) / 2;

  for (let i = 0; i < idCards.length; i++) {
    const slot = i % perPage;
    if (slot === 0 && i > 0) pdf.addPage();
    pdf.addImage(idCards[i].idCardDataUrl, "PNG", x, margin + slot * (height + gap), width, height);
    if (onCardAdded) await onCardAdded(i);
  }
  return pdf;
};

const GenerateStaffIDCardModal = ({ isOpen, onClose, teachers = [], schoolId, school }) => {
  const [selectedTeachers, setSelectedTeachers] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState("");

  // Progress modal states
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [progress, setProgress] = useState(0);
  const [stepName, setStepName] = useState("");
  const [completedSteps, setCompletedSteps] = useState([]);
  const [progressError, setProgressError] = useState("");
  const [isDownloading, setIsDownloading] = useState(false);
  const [generatedIDCards, setGeneratedIDCards] = useState([]);
  const [resultsSummary, setResultsSummary] = useState([]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setSelectedTeachers([]);
      setSearchText("");
      setError("");
      setShowProgressModal(false);
      setCurrentStep(0);
      setProgress(0);
      setCompletedSteps([]);
      setProgressError("");
      setIsDownloading(false);
      setGeneratedIDCards([]);
      setResultsSummary([]);
    }
  }, [isOpen]);

  // Filtered teachers based on search query
  const filteredTeachers = useMemo(() => {
    if (!searchText.trim()) return teachers;
    const query = searchText.toLowerCase();
    return teachers.filter((t) => {
      const name = `${t.firstname || ""} ${t.lastname || ""}`.toLowerCase();
      const email = (t.email || "").toLowerCase();
      const staffId = (t.staff_id || t.staffId || "").toLowerCase();
      return name.includes(query) || email.includes(query) || staffId.includes(query);
    });
  }, [teachers, searchText]);

  const handleTeacherToggle = (teacherId) => {
    setSelectedTeachers((prev) =>
      prev.includes(teacherId) ? prev.filter((id) => id !== teacherId) : [...prev, teacherId],
    );
  };

  const handleSelectAll = () => {
    const visibleIds = filteredTeachers.map((t) => t._id);
    const allVisibleSelected =
      visibleIds.length > 0 && visibleIds.every((id) => selectedTeachers.includes(id));

    if (allVisibleSelected) {
      setSelectedTeachers((prev) => prev.filter((id) => !visibleIds.includes(id)));
    } else {
      setSelectedTeachers((prev) => Array.from(new Set([...prev, ...visibleIds])));
    }
  };

  const updateProgress = (step, progressValue, stepNameValue) => {
    setCurrentStep(step);
    setProgress(progressValue);
    setStepName(stepNameValue);
  };

  const completeStep = (step) => {
    setCompletedSteps((prev) => [...prev, step]);
    setProgress(100);
  };

  const handleGenerateStaffIDs = async () => {
    if (selectedTeachers.length === 0) {
      setError("Please select at least one staff member.");
      return;
    }

    try {
      setIsGenerating(true);
      setError("");
      setShowProgressModal(true);
      setCurrentStep(0);
      setProgress(0);
      setCompletedSteps([]);
      setProgressError("");
      setResultsSummary([]);

      // Step 0: Call backend generate batch endpoint
      updateProgress(
        0,
        20,
        `Generating ID card credentials for ${selectedTeachers.length} staff...`,
      );

      const { data } = await userRequest.post(`/clocking/${schoolId}/teachers/generate`, {
        teacherIds: selectedTeachers,
      });

      const backendResults = data?.results || [];
      const successfulResults = backendResults.filter(
        (r) => r.success && r.card && r.card.qrCodeData,
      );

      // Build summary tracking items. A result only counts as a real success here
      // if it also has a usable card + qrCodeData to render — otherwise no card
      // will ever be produced for it, and the summary must reflect that up front
      // rather than relying on the render loop below to catch it.
      const initialSummary = selectedTeachers.map((id) => {
        const staff = teachers.find((t) => t._id === id);
        const name = staff ? `${staff.firstname} ${staff.lastname}` : id;
        const result = backendResults.find((r) => r.teacherId === id);
        const hasRenderableCard = Boolean(result?.success && result?.card && result?.card?.qrCodeData);
        return {
          teacherId: id,
          name,
          success: hasRenderableCard,
          message:
            result?.message ||
            (hasRenderableCard
              ? "Success"
              : result?.success
                ? "Card generated without a scannable QR code"
                : "Failed to generate credentials"),
          card: result?.card,
          staff,
        };
      });
      setResultsSummary(initialSummary);

      if (successfulResults.length === 0) {
        throw new Error("No staff credentials could be generated. Please review the results.");
      }

      updateProgress(
        0,
        100,
        `Credentials generated (${successfulResults.length}/${selectedTeachers.length} successful)`,
      );
      completeStep(0);

      await new Promise((resolve) => setTimeout(resolve, 400));

      // Step 1: Create Canvas ID Card Templates
      updateProgress(1, 0, "Composing Staff ID card templates...");

      const idCards = [];
      const currentSummary = [...initialSummary];

      for (let i = 0; i < successfulResults.length; i++) {
        const result = successfulResults[i];
        const staff = teachers.find((t) => t._id === result.teacherId) || {
          _id: result.teacherId,
          firstname: result.card?.firstname,
          lastname: result.card?.lastname,
          staff_id: result.card?.staffId,
          photo: result.card?.photo,
          picture: result.card?.photo ? { path: result.card.photo } : undefined,
        };

        const staffName =
          `${staff.firstname || ""} ${staff.lastname || ""}`.trim() || result.teacherId;

        if (result.card?.qrCodeData) {
          try {
            const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=600x600&data=${encodeURIComponent(result.card.qrCodeData)}`;

            const staffForCard = {
              ...staff,
              staff_id: result.card.staffId || staff.staff_id,
              firstname: result.card.firstname || staff.firstname,
              lastname: result.card.lastname || staff.lastname,
              photo: result.card.photo || staff.photo || staff.picture?.path,
            };

            const idCardDataUrl = await generateStaffIDCard(staffForCard, school, qrCodeUrl);

            if (idCardDataUrl) {
              idCards.push({
                staff: staffForCard,
                qrCodeData: result.card.qrCodeData,
                idCardDataUrl,
                staffId: result.card.staffId,
              });
            } else {
              // Canvas failed
              const itemIdx = currentSummary.findIndex((s) => s.teacherId === result.teacherId);
              if (itemIdx !== -1) {
                currentSummary[itemIdx].success = false;
                currentSummary[itemIdx].message = "Canvas generation failed";
              }
            }
          } catch (cardError) {
            console.error(`Error creating ID card for ${staffName}:`, cardError);
            const itemIdx = currentSummary.findIndex((s) => s.teacherId === result.teacherId);
            if (itemIdx !== -1) {
              currentSummary[itemIdx].success = false;
              currentSummary[itemIdx].message = cardError.message || "Render failed";
            }
          }
        }

        const cardProgress = Math.round(((i + 1) / successfulResults.length) * 100);
        updateProgress(1, cardProgress, `Composing card for ${staffName}...`);
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      setResultsSummary(currentSummary);

      if (idCards.length === 0) {
        throw new Error("Failed to render ID cards for all selected staff.");
      }

      updateProgress(1, 100, "ID card templates composed successfully");
      completeStep(1);
      setGeneratedIDCards(idCards);

      await new Promise((resolve) => setTimeout(resolve, 400));

      // Step 2: Assemble PDF (cards at true print size, stacked on A4 pages)
      updateProgress(2, 0, "Assembling print-ready PDF...");
      await buildStaffIDCardsPdf(idCards, async (index) => {
        const idCard = idCards[index];
        updateProgress(
          2,
          Math.round(((index + 1) / idCards.length) * 100),
          `Adding ${idCard.staff.firstname} ${idCard.staff.lastname} to PDF...`,
        );
        await new Promise((resolve) => setTimeout(resolve, 100));
      });

      updateProgress(2, 100, "PDF assembled successfully");
      completeStep(2);

      await new Promise((resolve) => setTimeout(resolve, 400));

      // Step 3: Ready for Download
      updateProgress(3, 100, "Staff ID cards ready for download");
      completeStep(3);
    } catch (err) {
      console.error("Error generating staff ID cards:", err);
      setProgressError(err.message || "Failed to generate staff ID cards. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRetry = () => {
    setProgressError("");
    setCompletedSteps([]);
    setCurrentStep(0);
    setProgress(0);
    handleGenerateStaffIDs();
  };

  const handleDownloadPDF = async () => {
    if (generatedIDCards.length === 0) return;

    try {
      setIsDownloading(true);

      const pdf = await buildStaffIDCardsPdf(generatedIDCards);

      const schoolSafeName = (school?.school_name || "School").replace(/[^a-zA-Z0-9_-]/g, "_");
      const filename = `${schoolSafeName}_Staff_ID_Cards_${format(new Date(), "yyyy-MM-dd")}.pdf`;
      pdf.save(filename);
    } catch (err) {
      console.error("Error downloading staff ID PDF:", err);
    } finally {
      setIsDownloading(false);
    }
  };

  if (!isOpen) return null;

  const visibleIds = filteredTeachers.map((t) => t._id);
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedTeachers.includes(id));

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 sm:p-4">
        <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-2xl flex flex-col max-h-[90vh] shadow-2xl">
          {/* Header */}
          <div className="bg-slate-50 border-b border-slate-200 px-6 py-4 flex-shrink-0">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-indigo-100 text-indigo-700 rounded-xl flex items-center justify-center flex-shrink-0">
                  <FiCreditCard className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-lg md:text-xl font-bold text-slate-800">
                    Generate Staff ID Cards
                  </h2>
                  <p className="text-slate-500 text-xs md:text-sm">
                    Select staff members to issue printable QR ID cards
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-slate-500 hover:bg-slate-200 p-2 rounded-lg transition-colors flex-shrink-0"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Search & Quick Select Bar */}
          <div className="p-5 border-b border-slate-200 bg-white space-y-3 flex-shrink-0">
            <div className="flex items-center justify-between gap-3">
              <div className="relative flex-1">
                <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  placeholder="Search staff by name, email, or ID..."
                  className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <button
                type="button"
                onClick={handleSelectAll}
                className="px-3.5 py-2 text-xs font-semibold text-[#3F3DA5] bg-indigo-50 hover:bg-indigo-100 rounded-xl transition-colors whitespace-nowrap"
              >
                {allVisibleSelected ? "Deselect All" : "Select All"}
              </button>
            </div>

            <div className="flex items-center justify-between text-xs text-slate-500 px-1">
              <span>
                Selected: <strong className="text-slate-800">{selectedTeachers.length}</strong> of{" "}
                {teachers.length} staff
              </span>
              {searchText && <span>Showing {filteredTeachers.length} matching</span>}
            </div>

            {error && (
              <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg p-2.5">
                {error}
              </div>
            )}
          </div>

          {/* Staff Checkbox Grid */}
          <div className="p-5 overflow-y-auto flex-1 divide-y divide-slate-100">
            {filteredTeachers.length === 0 ? (
              <div className="text-center py-10 text-slate-400">
                <FiUsers className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p className="text-sm font-medium">No staff members found</p>
              </div>
            ) : (
              <div className="space-y-1.5">
                {filteredTeachers.map((staff) => {
                  const isSelected = selectedTeachers.includes(staff._id);
                  const roleLabel = ROLE_LABELS[staff.role] || staff.role || "Staff";
                  const staffIdDisplay = staff.staff_id || staff.staffId;

                  return (
                    <div
                      key={staff._id}
                      onClick={() => handleTeacherToggle(staff._id)}
                      className={`flex items-center justify-between p-3 rounded-xl cursor-pointer transition-all ${
                        isSelected
                          ? "bg-indigo-50 border border-indigo-200"
                          : "hover:bg-slate-50 border border-transparent"
                      }`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div
                          className={`w-5 h-5 rounded flex items-center justify-center border transition-colors ${
                            isSelected
                              ? "bg-[#3F3DA5] border-[#3F3DA5] text-white"
                              : "border-slate-300 bg-white"
                          }`}
                        >
                          {isSelected && <FiCheck className="w-3.5 h-3.5 stroke-[3]" />}
                        </div>
                        <div className="truncate">
                          <p className="font-semibold text-slate-800 text-sm truncate">
                            {staff.firstname} {staff.lastname}
                          </p>
                          <p className="text-xs text-slate-500 truncate">
                            {staff.email} •{" "}
                            <span className="text-indigo-600 font-medium">{roleLabel}</span>
                            {staffIdDisplay && (
                              <span className="ml-1 text-slate-400 font-mono">
                                ({staffIdDisplay})
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer Actions */}
          <div className="bg-slate-50 border-t border-slate-200 px-6 py-4 flex justify-between items-center flex-shrink-0">
            <span className="text-xs text-slate-500">
              {selectedTeachers.length} card{selectedTeachers.length === 1 ? "" : "s"} to be
              generated
            </span>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-xs sm:text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-full hover:bg-slate-100 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleGenerateStaffIDs}
                disabled={selectedTeachers.length === 0 || isGenerating}
                className="px-5 py-2 text-xs sm:text-sm font-medium text-white bg-[#3F3DA5] hover:bg-indigo-900 rounded-full shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
              >
                <FiCreditCard className="w-4 h-4" />
                <span>
                  Generate {selectedTeachers.length > 0 ? `(${selectedTeachers.length})` : ""}
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Progress & Results Modal */}
      <BulkIDGenerationProgressModal
        isOpen={showProgressModal}
        onClose={() => {
          setShowProgressModal(false);
          onClose();
        }}
        currentStep={currentStep}
        totalSteps={4}
        stepName={stepName}
        progress={progress}
        completedSteps={completedSteps}
        error={progressError}
        onDownloadPDF={handleDownloadPDF}
        onRetry={handleRetry}
        isDownloading={isDownloading}
        title="Generating Staff ID Cards"
        subtitle="Creating signed QR codes and printable ID cards"
        successTitle="Staff ID Cards Generated Successfully!"
        successSubtitle={`Print-ready PDF with ${generatedIDCards.length} ID cards is ready`}
        results={resultsSummary}
      />
    </>
  );
};

export default GenerateStaffIDCardModal;
